import type { Assignment } from '../api/generated/Assignment'
import type { ConversationSummary } from '../api/generated/ConversationSummary'
import type { GroupSummary } from '../api/generated/GroupSummary'
import type { Position } from '../api/generated/Position'
import type { Shift } from '../api/generated/Shift'
import type { Volunteer } from '../api/generated/Volunteer'
import { volunteerName } from '../assignments/status'
import { fillOf, sortShifts } from '../shifts/fill'
import { plural } from '../volunteers/rows'

const DAY_MS = 86_400_000
/** How far ahead a shift without volunteers becomes a thing to do. */
const SOON_DAYS = 7
const MAX_NAMES = 3

/** Volunteers accepted on no shift, for the organizers to assign. */
export function volunteersToAssign(volunteers: Volunteer[]): Volunteer[] {
  return volunteers.filter((volunteer) => volunteer.accepted_shifts === 0)
}

export function unreadMessages(
  conversations: ConversationSummary[],
  groups: GroupSummary[],
): number {
  return [...conversations, ...groups].reduce((sum, { unread }) => sum + unread, 0)
}

/** Volunteers accepted on at least one shift. */
export function validatedVolunteers(assignments: Assignment[]): number {
  return new Set(assignments.filter((a) => a.status === 'accepted').map((a) => a.user_id)).size
}

const datesFormat = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})

const asDate = (day: string) => new Date(`${day}T00:00:00Z`)

/** `6–7 févr. 2027` for the planning's days, in order. */
export function eventDates(days: string[]): string | undefined {
  const first = days[0]
  const last = days[days.length - 1]
  if (!first || !last) return undefined
  return datesFormat.formatRange(asDate(first), asDate(last))
}

/** `J-12` before the event, `En cours` during it, `Terminé` after. */
export function countdown(days: string[], today: string): string | undefined {
  const first = days[0]
  const last = days[days.length - 1]
  if (!first || !last) return undefined
  if (today > last) return 'Terminé'
  if (today >= first) return 'En cours'
  return `J-${Math.round((asDate(first).getTime() - asDate(today).getTime()) / DAY_MS)}`
}

export type TodoKind =
  | 'volunteers-to-assign'
  | 'empty-shifts'
  | 'applications-closed'
  | 'no-positions'
  | 'no-shifts'
  | 'no-description'

export type TodoItem = {
  kind: TodoKind
  key: string
  title: string
  detail: string
  action: string
  href: string
  /** Needs action soon, rather than preparation. */
  urgent: boolean
}

function names(list: string[]): string {
  const distinct = [...new Set(list)]
  const shown = distinct.slice(0, MAX_NAMES).join(', ')
  return distinct.length > MAX_NAMES ? `${shown}…` : shown
}

/**
 * What the organizers should do next, urgent first, each with the page that does it.
 * `now` is the local time of the event, in the format of shift times.
 */
export function todoItems({
  base,
  positions,
  shifts,
  volunteers,
  applicationsOpen,
  now,
}: {
  /** `/events/{slug}` */
  base: string
  positions: Position[]
  shifts: Shift[]
  volunteers: Volunteer[]
  applicationsOpen: boolean
  now: string
}): TodoItem[] {
  const items: TodoItem[] = []
  const nameOf = (shift: Shift) => positions.find((p) => p.id === shift.position_id)?.name ?? ''

  const toAssign = volunteersToAssign(volunteers)
  if (toAssign.length > 0)
    items.push({
      kind: 'volunteers-to-assign',
      key: 'volunteers-to-assign',
      title: `${plural(toAssign.length, 'bénévole', 'bénévoles')} à répartir`,
      detail: names(toAssign.map(volunteerName)),
      action: 'Répartir',
      href: `${base}/volunteers`,
      urgent: true,
    })

  const horizon = new Date(new Date(`${now}Z`).getTime() + SOON_DAYS * DAY_MS)
    .toISOString()
    .slice(0, 19)
  const empty = sortShifts(shifts).filter(
    (shift) =>
      shift.accepted_volunteers === 0 && shift.starts_at > now && shift.starts_at <= horizon,
  )
  if (empty.length > 0)
    items.push({
      kind: 'empty-shifts',
      key: 'empty-shifts',
      title: `${plural(empty.length, 'créneau', 'créneaux')} sans personne dans les ${SOON_DAYS} jours`,
      detail: names(empty.map(nameOf)),
      action: 'Voir le planning',
      href: `${base}/planning`,
      urgent: true,
    })

  const upcoming = shifts.filter((shift) => shift.ends_at > now)
  const fill = fillOf(upcoming)
  if (!applicationsOpen && fill.accepted < fill.required)
    items.push({
      kind: 'applications-closed',
      key: 'applications-closed',
      title: 'Inscriptions fermées',
      detail: `${plural(fill.required - fill.accepted, 'place', 'places')} à pourvoir`,
      action: 'Ouvrir',
      href: `${base}/volunteers`,
      urgent: false,
    })

  if (positions.length === 0)
    items.push({
      kind: 'no-positions',
      key: 'no-positions',
      title: 'Aucun poste',
      detail: 'Créez les postes, puis leurs créneaux',
      action: 'Créer un poste',
      href: `${base}/positions/new`,
      urgent: false,
    })

  for (const position of positions) {
    const href = `${base}/positions/${position.id}`
    if (!shifts.some((shift) => shift.position_id === position.id))
      items.push({
        kind: 'no-shifts',
        key: `no-shifts-${position.id}`,
        title: `Poste « ${position.name} » sans créneau`,
        detail: 'Ajoutez ses créneaux pour recruter',
        action: 'Ajouter',
        href,
        urgent: false,
      })
    if (!position.job_description.trim())
      items.push({
        kind: 'no-description',
        key: `no-description-${position.id}`,
        title: `Fiche de poste « ${position.name} » vide`,
        detail: 'Les bénévoles ne savent pas encore quoi faire',
        action: 'Compléter',
        href,
        urgent: false,
      })
  }
  return items
}
